import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';

// デバッグモードに指定するフラグのUnion型。 'example1' | 'example2' のように Union 型で指定する。
type DebugMode =
  | 'example1'
  | 'test-error'
  | 'test-error-boundary'
  | 'test-trpc-hello'
  | 'zone-shape'
  | 'perf'
  | 'all-ai-models'
  | 'force-edit-tool-error'
  | 'ai-modeling-effect';

// SPA を最初にロードした時点の debug クエリパラメータ。
// SPA 内遷移の途中では URL 上から debug が一時的に消えるため、判定はここに固定した値だけを参照する。
const initialDebugMode = new URLSearchParams(window.location.search).get('debug') || null;

type Props = {
  children: ReactElement;
};

/**
 * Balus の SPA を最初にロードした時点に設定されていた debug クエリパラメータをSPA内部のページ遷移時に付与し続けるコンテナコンポーネント。
 *
 * このコンテナコンポーネントを、 react-router の Router（または現在利用している Router 実装）の内部の最上位に追加することで、
 * SPA内部のページ移動をトリガーに移動後のURLに debug クエリパラメータの付与を行う。
 */
// ts-unused-exports:disable-next-line
export const WithDebugMode: React.FC<Props> = ({ children }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!initialDebugMode) {
      return;
    }

    const params = new URLSearchParams(location.search);
    if (!params.has('debug')) {
      params.append('debug', initialDebugMode);
    }
    const newSearchString = '?' + params.toString();

    if (location.search === newSearchString) {
      return;
    }

    // location.state を引き継がないと、直前の navigate(path, {state}) が上書きされて消えてしまう
    // (例: Microsoft/Google アカウント連携結果を AccountConnectedPage へ state で渡す処理)
    navigate({ ...location, search: newSearchString }, { replace: true, state: location.state });
  }, [navigate, location]);

  return children;
};

// ts-unused-exports:disable-next-line
export const isDebugMode = (mode: DebugMode): boolean => initialDebugMode === mode;
